import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, ChevronDown, ChevronRight, FileDown, StickyNote, Edit2, Save, X, Plus, Copy, Trash2, Layers, ChevronLeft, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { supabase, Transaction, Category, TreasuryScenario } from '../lib/supabase';
import { PDFExporter } from '../utils/pdfExport';
import { useAuth } from '../contexts/AuthContext';
import { syncBudgetToTreasury } from '../utils/budgetTreasurySync';
import { useFiscalYear } from '../hooks/useFiscalYear';

interface CategoryMonthData {
  categoryId: string;
  categoryName: string;
  monthData: {
    [monthKey: string]: {
      realise: number;
      previsionnel: number;
      percentage: number;
    };
  };
}

interface MonthInfo {
  month: number;
  year: number;
  calendarYear: number;
  monthName: string;
  isPast: boolean;
}

interface MonthTreasury {
  [monthKey: string]: {
    soldeRealise: number;
    soldePrevisionnel: number;
    tresorerieDebut: number;
    tresorerieFin: number;
  };
}

export function CashFlowTab() {
  const { effectiveUserId } = useAuth();
  const { startMonth } = useFiscalYear();

  function getCurrentFiscalYearStart(): Date {
    const now = new Date();
    const currentYear = now.getFullYear();
    const fiscalStartMonth = (startMonth || 9) - 1;

    let fiscalYear = currentYear;
    if (now.getMonth() < fiscalStartMonth) {
      fiscalYear = currentYear - 1;
    }

    return new Date(fiscalYear, fiscalStartMonth, 1);
  }

  const [startDate, setStartDate] = useState<Date>(getCurrentFiscalYearStart());
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [incomeCategories, setIncomeCategories] = useState<CategoryMonthData[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<CategoryMonthData[]>([]);
  const [months, setMonths] = useState<MonthInfo[]>([]);
  const [monthlyTreasury, setMonthlyTreasury] = useState<MonthTreasury>({});
  const [initialTreasury, setInitialTreasury] = useState(0);
  const [editingCell, setEditingCell] = useState<{ categoryId: string; monthKey: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editingInitialTreasury, setEditingInitialTreasury] = useState(false);
  const [initialTreasuryValue, setInitialTreasuryValue] = useState('');
  const [expandedSections, setExpandedSections] = useState<{ income: boolean; expense: boolean }>({
    income: true,
    expense: true
  });
  const [treasuryScenarios, setTreasuryScenarios] = useState<TreasuryScenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [showCreateScenarioModal, setShowCreateScenarioModal] = useState(false);
  const [showEditScenarioModal, setShowEditScenarioModal] = useState(false);
  const [newScenario, setNewScenario] = useState({ name: '', description: '' });
  const [editScenario, setEditScenario] = useState({ id: '', name: '', description: '' });
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showSyncConfirmModal, setShowSyncConfirmModal] = useState(false);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [syncOptions, setSyncOptions] = useState({
    syncIncome: true,
    syncExpense: true
  });
  const [isEssentialView, setIsEssentialView] = useState(false);

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  useEffect(() => {
    if (effectiveUserId) {
      loadScenarios();
      loadData();
    }
  }, [startDate, effectiveUserId, selectedScenario]);

  useEffect(() => {
    if (!effectiveUserId) return;

    const channel = supabase
      .channel('treasury-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `user_id=eq.${effectiveUserId}`
        },
        () => {
          loadData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cash_flow_forecast_lines',
          filter: `user_id=eq.${effectiveUserId}`
        },
        () => {
          loadData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cash_flow_forecasts',
          filter: `user_id=eq.${effectiveUserId}`
        },
        () => {
          loadData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [effectiveUserId, startDate, selectedScenario]);

  function getRollingMonths(start: Date): MonthInfo[] {
    const result: MonthInfo[] = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(start);
      date.setMonth(start.getMonth() + i);

      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const monthDate = new Date(year, month - 1);
      const isPast = monthDate < new Date(now.getFullYear(), now.getMonth());

      result.push({
        month,
        year,
        calendarYear: year,
        monthName: monthNames[month - 1],
        isPast
      });
    }

    return result;
  }

  function navigateYear(direction: number) {
    const newDate = new Date(startDate);
    newDate.setFullYear(startDate.getFullYear() + direction);
    setStartDate(newDate);
  }

  function goToCurrentFiscalYear() {
    setStartDate(getCurrentFiscalYearStart());
  }

  async function loadScenarios() {
    if (!effectiveUserId) return;

    const rollingMonths = getRollingMonths(startDate);
    const years = [...new Set(rollingMonths.map(m => m.year))];

    const { data, error } = await supabase
      .from('treasury_scenarios')
      .select('*')
      .eq('user_id', effectiveUserId)
      .in('year', years)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setTreasuryScenarios(data);

      if (selectedScenario !== null) {
        const scenarioExists = data.some(s => s.id === selectedScenario);
        if (!scenarioExists) {
          setSelectedScenario(null);
        }
      }
    }
  }

  async function loadData() {
    if (!effectiveUserId) return;

    const rollingMonths = getRollingMonths(startDate);
    const firstMonth = rollingMonths[0];
    const lastMonth = rollingMonths[11];

    const startDateStr = `${firstMonth.calendarYear}-${String(firstMonth.month).padStart(2, '0')}-01`;
    const endDateStr = `${lastMonth.calendarYear}-${String(lastMonth.month).padStart(2, '0')}-31`;

    const years = [...new Set(rollingMonths.map(m => m.year))];

    let forecastLinesQuery = supabase
      .from('cash_flow_forecast_lines')
      .select('*')
      .eq('user_id', effectiveUserId)
      .in('forecast_year', years);

    let forecastsQuery = supabase
      .from('cash_flow_forecasts')
      .select('*')
      .eq('user_id', effectiveUserId)
      .in('year', years);

    if (selectedScenario === null) {
      forecastLinesQuery = forecastLinesQuery.is('scenario_id', null);
      forecastsQuery = forecastsQuery.is('scenario_id', null);
    } else {
      forecastLinesQuery = forecastLinesQuery.eq('scenario_id', selectedScenario);
      forecastsQuery = forecastsQuery.eq('scenario_id', selectedScenario);
    }

    const [categoriesRes, transactionsRes, forecastLinesRes, forecastsRes] = await Promise.all([
      supabase
        .from('categories')
        .select('*')
        .eq('user_id', effectiveUserId)
        .order('name'),
      supabase
        .from('transactions')
        .select('*')
        .eq('user_id', effectiveUserId)
        .gte('date', startDateStr)
        .lte('date', endDateStr)
        .order('date'),
      forecastLinesQuery,
      forecastsQuery
    ]);

    if (categoriesRes.data) {
      setCategories(categoriesRes.data.filter(c => !c.is_internal_transfer));
    }

    if (transactionsRes.data) {
      const filteredTransactions = transactionsRes.data.filter((t: Transaction) => !t.is_internal_transfer);
      setTransactions(filteredTransactions);

      const uncategorized = filteredTransactions.filter((t: Transaction) => !t.category_id);
      setUncategorizedCount(uncategorized.length);
      if (uncategorized.length > 0) {
        console.log(`[CashFlow] ${uncategorized.length} transactions non catégorisées ne sont pas incluses dans le plan`);
      }
    }

    const monthsInfo = rollingMonths;
    setMonths(monthsInfo);

    const firstMonthOfYear = rollingMonths[0];
    const forecastWithTreasury = forecastsRes.data?.find(
      f => f.month === firstMonthOfYear.month &&
           f.year === firstMonthOfYear.calendarYear &&
           f.initial_treasury !== undefined &&
           f.initial_treasury !== null
    ) || forecastsRes.data?.find(f => f.initial_treasury !== undefined && f.initial_treasury !== null);

    const initialTreas = forecastWithTreasury && forecastWithTreasury.initial_treasury !== undefined
      ? Number(forecastWithTreasury.initial_treasury)
      : 0;

    setInitialTreasury(initialTreas);

    processData(
      categoriesRes.data || [],
      transactionsRes.data || [],
      forecastLinesRes.data || [],
      monthsInfo,
      initialTreas
    );
  }

  function processData(
    cats: Category[],
    trans: Transaction[],
    forecasts: any[],
    monthsInfo: MonthInfo[],
    initialTreas: number
  ) {
    const filteredCats = cats.filter(c => !c.is_internal_transfer);
    const filteredTrans = trans.filter(t => !t.is_internal_transfer);

    const incomeCats: CategoryMonthData[] = [];
    const expenseCats: CategoryMonthData[] = [];

    filteredCats.forEach(category => {
      const monthData: CategoryMonthData['monthData'] = {};

      monthsInfo.forEach(monthInfo => {
        const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
        const monthStart = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}-01`;
        const monthEnd = new Date(monthInfo.calendarYear, monthInfo.month, 0);
        const monthEndStr = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}-${String(monthEnd.getDate()).padStart(2, '0')}`;

        const realise = filteredTrans
          .filter(t =>
            t.category_id === category.id &&
            t.date >= monthStart &&
            t.date <= monthEndStr
          )
          .reduce((sum, t) => sum + Math.abs(t.amount), 0);

        const forecastLine = forecasts.find(
          f => f.category_id === category.id &&
               f.forecast_month === monthInfo.month &&
               f.forecast_year === monthInfo.calendarYear
        );
        const previsionnel = forecastLine ? Math.abs(Number(forecastLine.amount)) : 0;

        const percentage = previsionnel > 0 ? Math.round((realise / previsionnel) * 100) : 0;

        monthData[monthKey] = {
          realise,
          previsionnel,
          percentage
        };
      });

      const categoryData: CategoryMonthData = {
        categoryId: category.id,
        categoryName: category.name,
        monthData
      };

      if (category.type === 'income') {
        incomeCats.push(categoryData);
      } else {
        expenseCats.push(categoryData);
      }
    });

    setIncomeCategories(incomeCats);
    setExpenseCategories(expenseCats);

    const treasury: MonthTreasury = {};
    let currentTreasury = initialTreas;

    monthsInfo.forEach((monthInfo, idx) => {
      const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;

      const incomeRealise = incomeCats.reduce((sum, cat) => sum + (cat.monthData[monthKey]?.realise || 0), 0);
      const expenseRealise = expenseCats.reduce((sum, cat) => sum + (cat.monthData[monthKey]?.realise || 0), 0);
      const soldeRealise = incomeRealise - expenseRealise;

      const incomePrev = incomeCats.reduce((sum, cat) => sum + (cat.monthData[monthKey]?.previsionnel || 0), 0);
      const expensePrev = expenseCats.reduce((sum, cat) => sum + (cat.monthData[monthKey]?.previsionnel || 0), 0);
      const soldePrevisionnel = incomePrev - expensePrev;

      const tresorerieDebut = currentTreasury;
      const tresorerieFin = tresorerieDebut + soldePrevisionnel;

      treasury[monthKey] = {
        soldeRealise,
        soldePrevisionnel,
        tresorerieDebut,
        tresorerieFin
      };

      currentTreasury = tresorerieFin;
    });

    setMonthlyTreasury(treasury);
  }

  function startEditing(categoryId: string, monthKey: string, currentValue: number) {
    setEditingCell({ categoryId, monthKey });
    setEditValue(currentValue.toString());
  }

  async function saveEdit() {
    if (!effectiveUserId || !editingCell) return;

    const { categoryId, monthKey } = editingCell;
    const amount = parseFloat(editValue) || 0;
    const [yearStr, monthStr] = monthKey.split('-');
    const month = parseInt(monthStr);
    const year = parseInt(yearStr);

    const category = categories.find(c => c.id === categoryId);
    if (!category) return;

    let existingLineQuery = supabase
      .from('cash_flow_forecast_lines')
      .select('id')
      .eq('user_id', effectiveUserId)
      .eq('category_id', categoryId)
      .eq('forecast_month', month)
      .eq('forecast_year', year);

    if (selectedScenario === null) {
      existingLineQuery = existingLineQuery.is('scenario_id', null);
    } else {
      existingLineQuery = existingLineQuery.eq('scenario_id', selectedScenario);
    }

    const existingLine = await existingLineQuery.maybeSingle();

    if (existingLine.data) {
      await supabase
        .from('cash_flow_forecast_lines')
        .update({
          amount: category.type === 'income' ? amount : -amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingLine.data.id)
        .eq('user_id', effectiveUserId);
    } else {
      await supabase
        .from('cash_flow_forecast_lines')
        .insert({
          user_id: effectiveUserId,
          forecast_year: year,
          forecast_month: month,
          category_id: categoryId,
          type: category.type,
          amount: category.type === 'income' ? amount : -amount,
          description: category.name,
          scenario_id: selectedScenario
        });
    }

    setEditingCell(null);
    setEditValue('');
    loadData();
  }

  function cancelEdit() {
    setEditingCell(null);
    setEditValue('');
  }

  function startEditingInitialTreasury() {
    setEditingInitialTreasury(true);
    setInitialTreasuryValue(initialTreasury.toString());
  }

  async function saveInitialTreasury() {
    if (!effectiveUserId || months.length === 0) return;

    const value = parseFloat(initialTreasuryValue) || 0;
    const firstMonth = months[0];

    let existingForecastQuery = supabase
      .from('cash_flow_forecasts')
      .select('id')
      .eq('user_id', effectiveUserId)
      .eq('year', firstMonth.calendarYear)
      .eq('month', firstMonth.month);

    if (selectedScenario === null) {
      existingForecastQuery = existingForecastQuery.is('scenario_id', null);
    } else {
      existingForecastQuery = existingForecastQuery.eq('scenario_id', selectedScenario);
    }

    const existingForecast = await existingForecastQuery.maybeSingle();

    if (existingForecast.data) {
      await supabase
        .from('cash_flow_forecasts')
        .update({
          initial_treasury: value,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingForecast.data.id)
        .eq('user_id', effectiveUserId);
    } else {
      await supabase
        .from('cash_flow_forecasts')
        .insert({
          user_id: effectiveUserId,
          year: firstMonth.calendarYear,
          month: firstMonth.month,
          forecast_encaissements: 0,
          forecast_decaissements: 0,
          initial_treasury: value,
          scenario_id: selectedScenario
        });
    }

    setEditingInitialTreasury(false);
    setInitialTreasuryValue('');
    loadData();
  }

  async function createScenario() {
    if (!effectiveUserId || !newScenario.name.trim() || months.length === 0) return;

    const years = [...new Set(months.map(m => m.calendarYear))];
    const firstMonth = months[0];

    const { data: baseForecasts, error: baseForecastsError } = await supabase
      .from('cash_flow_forecasts')
      .select('*')
      .eq('user_id', effectiveUserId)
      .in('year', years)
      .is('scenario_id', null);

    const { data: baseForecastLines, error: baseForecastLinesError } = await supabase
      .from('cash_flow_forecast_lines')
      .select('*')
      .eq('user_id', effectiveUserId)
      .in('forecast_year', years)
      .is('scenario_id', null);

    const { data, error } = await supabase
      .from('treasury_scenarios')
      .insert([{
        user_id: effectiveUserId,
        name: newScenario.name,
        description: newScenario.description,
        year: firstMonth.calendarYear,
        is_active: false
      }])
      .select()
      .single();

    if (!error && data) {
      if (baseForecasts && baseForecasts.length > 0) {
        const forecastsToCopy = baseForecasts.map(f => ({
          user_id: effectiveUserId,
          year: f.year,
          month: f.month,
          forecast_encaissements: f.forecast_encaissements,
          forecast_decaissements: f.forecast_decaissements,
          initial_treasury: f.initial_treasury,
          scenario_id: data.id
        }));
        await supabase.from('cash_flow_forecasts').insert(forecastsToCopy);
      }

      if (baseForecastLines && baseForecastLines.length > 0) {
        const linesToCopy = baseForecastLines.map(l => ({
          user_id: effectiveUserId,
          forecast_year: l.forecast_year,
          forecast_month: l.forecast_month,
          description: l.description,
          amount: l.amount,
          category_id: l.category_id,
          type: l.type,
          scenario_id: data.id
        }));
        await supabase.from('cash_flow_forecast_lines').insert(linesToCopy);
      }

      loadScenarios();
      setNewScenario({ name: '', description: '' });
      setShowCreateScenarioModal(false);
      setSelectedScenario(data.id);
    }
  }

  async function duplicateScenario(scenario: TreasuryScenario) {
    if (!effectiveUserId || months.length === 0) return;

    const years = [...new Set(months.map(m => m.calendarYear))];
    const firstMonth = months[0];

    const { data: scenarioForecasts } = await supabase
      .from('cash_flow_forecasts')
      .select('*')
      .eq('user_id', effectiveUserId)
      .in('year', years)
      .eq('scenario_id', scenario.id);

    const { data: scenarioForecastLines } = await supabase
      .from('cash_flow_forecast_lines')
      .select('*')
      .eq('user_id', effectiveUserId)
      .in('forecast_year', years)
      .eq('scenario_id', scenario.id);

    const { data: newScenarioData, error: scenarioError } = await supabase
      .from('treasury_scenarios')
      .insert([{
        user_id: effectiveUserId,
        name: `${scenario.name} (copie)`,
        description: scenario.description,
        year: firstMonth.calendarYear,
        is_active: false
      }])
      .select()
      .single();

    if (!scenarioError && newScenarioData) {
      if (scenarioForecasts && scenarioForecasts.length > 0) {
        const forecastsToCopy = scenarioForecasts.map(f => ({
          user_id: effectiveUserId,
          year: f.year,
          month: f.month,
          forecast_encaissements: f.forecast_encaissements,
          forecast_decaissements: f.forecast_decaissements,
          initial_treasury: f.initial_treasury,
          scenario_id: newScenarioData.id
        }));
        await supabase.from('cash_flow_forecasts').insert(forecastsToCopy);
      }

      if (scenarioForecastLines && scenarioForecastLines.length > 0) {
        const linesToCopy = scenarioForecastLines.map(l => ({
          user_id: effectiveUserId,
          forecast_year: l.forecast_year,
          forecast_month: l.forecast_month,
          description: l.description,
          amount: l.amount,
          category_id: l.category_id,
          type: l.type,
          scenario_id: newScenarioData.id
        }));
        await supabase.from('cash_flow_forecast_lines').insert(linesToCopy);
      }

      loadScenarios();
      setSelectedScenario(newScenarioData.id);
    }
  }

  async function updateScenario() {
    if (!effectiveUserId || !editScenario.name.trim()) return;

    const { error } = await supabase
      .from('treasury_scenarios')
      .update({
        name: editScenario.name,
        description: editScenario.description
      })
      .eq('id', editScenario.id)
      .eq('user_id', effectiveUserId);

    if (!error) {
      loadScenarios();
      setShowEditScenarioModal(false);
      setEditScenario({ id: '', name: '', description: '' });
    }
  }

  async function deleteScenario(id: string) {
    if (!effectiveUserId) return;

    const { error } = await supabase
      .from('treasury_scenarios')
      .delete()
      .eq('id', id)
      .eq('user_id', effectiveUserId);

    if (!error) {
      loadScenarios();
      if (selectedScenario === id) {
        setSelectedScenario(null);
      }
    }
  }

  function openSyncConfirmModal() {
    setSyncOptions({
      syncIncome: true,
      syncExpense: true
    });
    setShowSyncConfirmModal(true);
  }

  async function confirmSyncBudgetToTreasury() {
    if (!effectiveUserId) return;

    setShowSyncConfirmModal(false);
    setSyncLoading(true);
    setSyncMessage(null);

    const currentYear = startDate.getFullYear();
    const result = await syncBudgetToTreasury(
      effectiveUserId,
      currentYear,
      startMonth || 9,
      selectedScenario,
      syncOptions.syncIncome,
      syncOptions.syncExpense
    );

    setSyncLoading(false);

    if (result.success) {
      const parts = [];
      if (syncOptions.syncIncome) parts.push('recettes');
      if (syncOptions.syncExpense) parts.push('dépenses');
      const synced = parts.join(' et ');

      setSyncMessage({
        type: 'success',
        text: `Synchronisation réussie ! ${result.linesUpdated} lignes de trésorerie mises à jour (${synced}).`
      });
      loadData();
      setTimeout(() => setSyncMessage(null), 5000);
    } else {
      setSyncMessage({
        type: 'error',
        text: result.error || 'Erreur lors de la synchronisation'
      });
    }
  }

  function cancelEditInitialTreasury() {
    setEditingInitialTreasury(false);
    setInitialTreasuryValue('');
  }

  function toggleSection(section: 'income' | 'expense') {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }

  function calculateTotals(categoryList: CategoryMonthData[], monthKey: string) {
    const realiseTotal = categoryList.reduce((sum, cat) => sum + (cat.monthData[monthKey]?.realise || 0), 0);
    const previsionnelTotal = categoryList.reduce((sum, cat) => sum + (cat.monthData[monthKey]?.previsionnel || 0), 0);
    const percentageTotal = previsionnelTotal > 0 ? Math.round((realiseTotal / previsionnelTotal) * 100) : 0;
    return { realiseTotal, previsionnelTotal, percentageTotal };
  }

  async function exportToPDF() {
    const exporter = new PDFExporter();
    await exporter.exportCashFlowForecast(
      months.map(m => m.monthName),
      incomeCategories,
      expenseCategories,
      months
    );
  }

  const treasuryValues = months.map(m => {
    const monthKey = `${m.calendarYear}-${String(m.month).padStart(2, '0')}`;
    return monthlyTreasury[monthKey]?.tresorerieFin || 0;
  });

  const maxTreasury = Math.max(...treasuryValues, initialTreasury);
  const minTreasury = Math.min(...treasuryValues, initialTreasury, 0);
  const range = maxTreasury - minTreasury || 1;

  const currentScenario = treasuryScenarios.find(s => s.id === selectedScenario);

  const formatPeriod = () => {
    if (months.length === 0) return '';
    const firstMonth = months[0];
    const lastMonth = months[months.length - 1];
    return `${firstMonth.monthName} ${firstMonth.calendarYear} - ${lastMonth.monthName} ${lastMonth.calendarYear}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Plan de Trésorerie</h2>
          <p className="text-sm text-gray-600 mt-1">{formatPeriod()}</p>
        </div>
        <div className="flex gap-3">
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg">
            <button
              onClick={() => navigateYear(-1)}
              className="p-2 hover:bg-gray-100 transition-colors rounded-l-lg"
              title="Exercice précédent"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={goToCurrentFiscalYear}
              className="px-3 py-2 text-sm font-medium hover:bg-gray-100 transition-colors border-x border-gray-300"
              title="Revenir à l'exercice actuel"
            >
              Exercice actuel
            </button>
            <button
              onClick={() => navigateYear(1)}
              className="p-2 hover:bg-gray-100 transition-colors rounded-r-lg"
              title="Exercice suivant"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEssentialView(!isEssentialView)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border ${
                isEssentialView
                  ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              title={isEssentialView ? "Afficher la vue détaillée" : "Afficher la vue essentielle"}
            >
              {isEssentialView ? <EyeOff size={18} /> : <Eye size={18} />}
              {isEssentialView ? 'Vue détaillée' : 'Vue essentielle'}
            </button>
            <button
              onClick={openSyncConfirmModal}
              disabled={syncLoading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Synchroniser les lignes budgétaires vers le plan de trésorerie"
            >
              <RefreshCw size={18} className={syncLoading ? 'animate-spin' : ''} />
              {syncLoading ? 'Synchronisation...' : 'Sync Budget'}
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FileDown size={18} />
              Exporter PDF
            </button>
          </div>
        </div>
      </div>

      {syncMessage && (
        <div className={`rounded-lg p-4 ${
          syncMessage.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-start gap-3">
            {syncMessage.type === 'success' ? (
              <TrendingUp className="flex-shrink-0 mt-0.5" size={20} />
            ) : (
              <X className="flex-shrink-0 mt-0.5" size={20} />
            )}
            <p className="text-sm">{syncMessage.text}</p>
          </div>
        </div>
      )}

      {uncategorizedCount > 0 && (
        <div className="rounded-lg p-4 bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <StickyNote className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-amber-900">
              <p className="font-medium">
                {uncategorizedCount} transaction{uncategorizedCount > 1 ? 's' : ''} non catégorisée{uncategorizedCount > 1 ? 's' : ''}
              </p>
              <p className="mt-1 text-amber-800">
                Ces transactions ne sont pas incluses dans les montants "Réalisé" du plan de trésorerie.
                Catégorisez-les dans l'onglet "Transactions" pour qu'elles apparaissent automatiquement ici.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers size={20} className="text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">Scénarios de trésorerie</h3>
          </div>
          <button
            onClick={() => setShowCreateScenarioModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus size={18} />
            Nouveau scénario
          </button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedScenario(null)}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedScenario === null
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Prévisionnel de base
          </button>
          {treasuryScenarios.map(scenario => (
            <div
              key={scenario.id}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                selectedScenario === scenario.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <button
                onClick={() => setSelectedScenario(scenario.id)}
                className="flex-1"
              >
                {scenario.name}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditScenario({ id: scenario.id, name: scenario.name, description: scenario.description });
                  setShowEditScenarioModal(true);
                }}
                className={`p-1 rounded hover:bg-opacity-20 hover:bg-white ${
                  selectedScenario === scenario.id ? 'text-white' : 'text-gray-600'
                }`}
                title="Renommer"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  duplicateScenario(scenario);
                }}
                className={`p-1 rounded hover:bg-opacity-20 hover:bg-white ${
                  selectedScenario === scenario.id ? 'text-white' : 'text-blue-600'
                }`}
                title="Dupliquer"
              >
                <Copy size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteScenario(scenario.id);
                }}
                className={`p-1 rounded hover:bg-opacity-20 hover:bg-white ${
                  selectedScenario === scenario.id ? 'text-white' : 'text-red-600'
                }`}
                title="Supprimer"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        {currentScenario && currentScenario.description && (
          <p className="mt-3 text-sm text-gray-600 italic">{currentScenario.description}</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">
            Trésorerie de départ ({months.length > 0 ? months[0].monthName : 'Septembre'}) :
          </span>
          {editingInitialTreasury ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={initialTreasuryValue}
                onChange={(e) => setInitialTreasuryValue(e.target.value)}
                className="px-3 py-1 border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
              <button
                onClick={saveInitialTreasury}
                className="p-1 text-green-600 hover:bg-green-50 rounded"
              >
                <Save size={18} />
              </button>
              <button
                onClick={cancelEditInitialTreasury}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-blue-600">
                {initialTreasury.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
              </span>
              <button
                onClick={startEditingInitialTreasury}
                className="p-1 text-gray-600 hover:bg-gray-100 rounded"
              >
                <Edit2 size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Évolution de la trésorerie</h3>
        <div className="relative h-48">
          <svg className="w-full h-full" viewBox="0 0 1200 200" preserveAspectRatio="none">
            <line x1="0" y1="100" x2="1200" y2="100" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />

            <polyline
              points={months.map((m, idx) => {
                const monthKey = `${m.calendarYear}-${String(m.month).padStart(2, '0')}`;
                const value = monthlyTreasury[monthKey]?.tresorerieFin || 0;
                const x = (idx / (months.length - 1)) * 1200;
                const y = 180 - ((value - minTreasury) / range) * 160;
                return `${x},${y}`;
              }).join(' ')}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="3"
            />

            {months.map((m, idx) => {
              const monthKey = `${m.calendarYear}-${String(m.month).padStart(2, '0')}`;
              const value = monthlyTreasury[monthKey]?.tresorerieFin || 0;
              const x = (idx / (months.length - 1)) * 1200;
              const y = 180 - ((value - minTreasury) / range) * 160;
              return (
                <g key={idx}>
                  <circle cx={x} cy={y} r="5" fill={value >= 0 ? '#3b82f6' : '#ef4444'} />
                </g>
              );
            })}
          </svg>
          <div className="absolute bottom-0 left-0 right-0 flex justify-between px-2 text-[10px] text-gray-500">
            {months.map((m, idx) => (
              <span key={idx}>{m.monthName.substring(0, 3)}</span>
            ))}
          </div>
        </div>
      </div>

      {isEssentialView && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800">Trésorerie minimale</span>
              <TrendingDown className="text-blue-600" size={20} />
            </div>
            <div className={`text-2xl font-bold ${minTreasury < 0 ? 'text-red-700' : 'text-blue-700'}`}>
              {minTreasury.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
            </div>
            <p className="text-xs text-blue-700 mt-1">Point le plus bas de l'exercice</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-800">Trésorerie maximale</span>
              <TrendingUp className="text-green-600" size={20} />
            </div>
            <div className="text-2xl font-bold text-green-700">
              {maxTreasury.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
            </div>
            <p className="text-xs text-green-700 mt-1">Point le plus haut de l'exercice</p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-5 border border-teal-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-teal-800">Solde moyen mensuel</span>
              <TrendingUp className="text-teal-600" size={20} />
            </div>
            <div className="text-2xl font-bold text-teal-700">
              {(() => {
                const avgSolde = months.reduce((sum, m) => {
                  const monthKey = `${m.calendarYear}-${String(m.month).padStart(2, '0')}`;
                  return sum + (monthlyTreasury[monthKey]?.soldePrevisionnel || 0);
                }, 0) / months.length;
                return avgSolde.toLocaleString('fr-FR', { maximumFractionDigits: 0 });
              })()}  €
            </div>
            <p className="text-xs text-teal-700 mt-1">Moyenne des soldes mensuels</p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-5 border border-orange-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-orange-800">Mois en déficit</span>
              <TrendingDown className="text-orange-600" size={20} />
            </div>
            <div className={`text-2xl font-bold ${
              months.filter(m => {
                const monthKey = `${m.calendarYear}-${String(m.month).padStart(2, '0')}`;
                return (monthlyTreasury[monthKey]?.tresorerieFin || 0) < 0;
              }).length > 0 ? 'text-red-700' : 'text-green-700'
            }`}>
              {months.filter(m => {
                const monthKey = `${m.calendarYear}-${String(m.month).padStart(2, '0')}`;
                return (monthlyTreasury[monthKey]?.tresorerieFin || 0) < 0;
              }).length} / {months.length}
            </div>
            <p className="text-xs text-orange-700 mt-1">Mois avec trésorerie négative</p>
          </div>
        </div>
      )}

      {isEssentialView ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-200 min-w-[200px]">
                    Vue essentielle
                  </th>
                  {months.map((monthInfo, idx) => (
                    <th key={idx} className="px-3 py-3 text-center text-xs font-semibold text-gray-700 border-r border-gray-200 min-w-[140px]">
                      <div className="space-y-1">
                        <div className="font-bold">{monthInfo.monthName}</div>
                        <div className="text-[10px] text-gray-500">{monthInfo.calendarYear}</div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-green-50 border-b border-gray-200">
                  <td className="px-4 py-3 font-bold text-green-800 border-r border-gray-200">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={18} />
                      <span>Encaissements totaux</span>
                    </div>
                  </td>
                  {months.map((monthInfo, idx) => {
                    const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                    const { previsionnelTotal } = calculateTotals(incomeCategories, monthKey);
                    return (
                      <td key={idx} className="px-3 py-3 text-center border-r border-gray-200">
                        <div className="text-sm font-bold text-green-700">
                          {previsionnelTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </div>
                      </td>
                    );
                  })}
                </tr>

                <tr className="bg-red-50 border-b border-gray-200">
                  <td className="px-4 py-3 font-bold text-red-800 border-r border-gray-200">
                    <div className="flex items-center gap-2">
                      <TrendingDown size={18} />
                      <span>Décaissements totaux</span>
                    </div>
                  </td>
                  {months.map((monthInfo, idx) => {
                    const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                    const { previsionnelTotal } = calculateTotals(expenseCategories, monthKey);
                    return (
                      <td key={idx} className="px-3 py-3 text-center border-r border-gray-200">
                        <div className="text-sm font-bold text-red-700">
                          {previsionnelTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </div>
                      </td>
                    );
                  })}
                </tr>

                <tr className="bg-blue-50 border-b-2 border-blue-200">
                  <td className="px-4 py-3 font-bold text-blue-800 border-r border-gray-200">
                    Solde du mois
                  </td>
                  {months.map((monthInfo, idx) => {
                    const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                    const treasury = monthlyTreasury[monthKey];
                    return (
                      <td key={idx} className="px-3 py-3 text-center border-r border-gray-200">
                        <div className="text-sm font-bold">
                          <span className={treasury?.soldePrevisionnel >= 0 ? 'text-green-700' : 'text-red-700'}>
                            {(treasury?.soldePrevisionnel || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>

                <tr className="bg-blue-100 border-b-2 border-blue-300">
                  <td className="px-4 py-3 font-bold text-blue-900 border-r border-gray-200">
                    Trésorerie fin de mois
                  </td>
                  {months.map((monthInfo, idx) => {
                    const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                    const treasury = monthlyTreasury[monthKey];
                    return (
                      <td key={idx} className="px-3 py-3 text-center border-r border-gray-200">
                        <div className="text-base font-bold">
                          <span className={treasury?.tresorerieFin >= 0 ? 'text-blue-700' : 'text-red-700'}>
                            {(treasury?.tresorerieFin || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-200 min-w-[200px]">
                    Catégories
                  </th>
                  {months.map((monthInfo, idx) => (
                    <th key={idx} className="px-3 py-3 text-center text-xs font-semibold text-gray-700 border-r border-gray-200 min-w-[180px]">
                      <div className="space-y-1">
                        <div className="font-bold">{monthInfo.monthName}</div>
                        <div className="text-[10px] text-gray-500">{monthInfo.calendarYear}</div>
                        <div className="flex justify-around text-[10px] text-gray-500 mt-1">
                          <span>Réalisé</span>
                          <span>Prév.</span>
                          <span>%</span>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
            <tbody>
              <tr className="bg-green-50 border-b border-gray-200">
                <td
                  className="sticky left-0 z-10 bg-green-50 px-4 py-3 font-bold text-green-800 cursor-pointer hover:bg-green-100 border-r border-gray-200"
                  onClick={() => toggleSection('income')}
                >
                  <div className="flex items-center gap-2">
                    {expandedSections.income ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <TrendingUp size={18} />
                    <span>Encaissements</span>
                  </div>
                </td>
                {months.map((monthInfo, idx) => {
                  const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                  const { realiseTotal, previsionnelTotal, percentageTotal } = calculateTotals(incomeCategories, monthKey);
                  return (
                    <td key={idx} className="px-3 py-3 text-center border-r border-gray-200">
                      <div className="flex justify-around items-center text-xs">
                        <span className="font-bold text-green-700">{realiseTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</span>
                        <span className="text-gray-600">{previsionnelTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</span>
                        <span className={`font-bold ${percentageTotal >= 98 ? 'text-green-600' : percentageTotal >= 80 ? 'text-orange-600' : 'text-red-600'}`}>
                          {previsionnelTotal > 0 ? `${percentageTotal}%` : '-'}
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>

              {expandedSections.income && incomeCategories.map((category) => (
                <tr key={category.categoryId} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="sticky left-0 z-10 bg-white hover:bg-gray-50 px-4 py-2 text-sm text-gray-700 border-r border-gray-200">
                    <div className="flex items-center gap-2 pl-6">
                      <ChevronRight size={14} className="text-gray-400" />
                      {category.categoryName}
                    </div>
                  </td>
                  {months.map((monthInfo, idx) => {
                    const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                    const data = category.monthData[monthKey];
                    const isEditing = editingCell?.categoryId === category.categoryId && editingCell?.monthKey === monthKey;

                    return (
                      <td key={idx} className="px-3 py-2 text-center border-r border-gray-200">
                        <div className="flex justify-around items-center text-xs">
                          <span className="text-gray-700">{data.realise.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</span>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              autoFocus
                              className="w-16 px-1 py-0.5 text-center border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          ) : (
                            <span
                              className="text-gray-600 cursor-pointer hover:bg-blue-50 px-1 rounded"
                              onClick={() => startEditing(category.categoryId, monthKey, data.previsionnel)}
                            >
                              {data.previsionnel.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
                            </span>
                          )}
                          <span className={`font-medium ${data.percentage >= 98 ? 'text-green-600' : data.percentage >= 80 ? 'text-orange-600' : 'text-red-600'}`}>
                            {data.previsionnel > 0 ? `${data.percentage}%` : '-'}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}

              <tr className="bg-red-50 border-b border-gray-200">
                <td
                  className="sticky left-0 z-10 bg-red-50 px-4 py-3 font-bold text-red-800 cursor-pointer hover:bg-red-100 border-r border-gray-200"
                  onClick={() => toggleSection('expense')}
                >
                  <div className="flex items-center gap-2">
                    {expandedSections.expense ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <TrendingDown size={18} />
                    <span>Décaissements</span>
                  </div>
                </td>
                {months.map((monthInfo, idx) => {
                  const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                  const { realiseTotal, previsionnelTotal, percentageTotal } = calculateTotals(expenseCategories, monthKey);
                  return (
                    <td key={idx} className="px-3 py-3 text-center border-r border-gray-200">
                      <div className="flex justify-around items-center text-xs">
                        <span className="font-bold text-red-700">{realiseTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</span>
                        <span className="text-gray-600">{previsionnelTotal.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</span>
                        <span className={`font-bold ${percentageTotal <= 102 ? 'text-green-600' : percentageTotal <= 120 ? 'text-orange-600' : 'text-red-600'}`}>
                          {previsionnelTotal > 0 ? `${percentageTotal}%` : '-'}
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>

              {expandedSections.expense && expenseCategories.map((category) => (
                <tr key={category.categoryId} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="sticky left-0 z-10 bg-white hover:bg-gray-50 px-4 py-2 text-sm text-gray-700 border-r border-gray-200">
                    <div className="flex items-center gap-2 pl-6">
                      <ChevronRight size={14} className="text-gray-400" />
                      {category.categoryName}
                    </div>
                  </td>
                  {months.map((monthInfo, idx) => {
                    const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                    const data = category.monthData[monthKey];
                    const isEditing = editingCell?.categoryId === category.categoryId && editingCell?.monthKey === monthKey;

                    return (
                      <td key={idx} className="px-3 py-2 text-center border-r border-gray-200">
                        <div className="flex justify-around items-center text-xs">
                          <span className="text-gray-700">{data.realise.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</span>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit();
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              autoFocus
                              className="w-16 px-1 py-0.5 text-center border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          ) : (
                            <span
                              className="text-gray-600 cursor-pointer hover:bg-blue-50 px-1 rounded"
                              onClick={() => startEditing(category.categoryId, monthKey, data.previsionnel)}
                            >
                              {data.previsionnel.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
                            </span>
                          )}
                          <span className={`font-medium ${data.percentage <= 102 ? 'text-green-600' : data.percentage <= 120 ? 'text-orange-600' : 'text-red-600'}`}>
                            {data.previsionnel > 0 ? `${data.percentage}%` : '-'}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}

              <tr className="bg-blue-50 border-b-2 border-blue-200">
                <td className="sticky left-0 z-10 bg-blue-50 px-4 py-3 font-bold text-blue-800 border-r border-gray-200">
                  Solde du mois
                </td>
                {months.map((monthInfo, idx) => {
                  const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                  const treasury = monthlyTreasury[monthKey];
                  return (
                    <td key={idx} className="px-3 py-3 text-center border-r border-gray-200">
                      <div className="flex justify-around items-center text-xs font-bold">
                        <span className={treasury?.soldeRealise >= 0 ? 'text-green-700' : 'text-red-700'}>
                          {(treasury?.soldeRealise || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
                        </span>
                        <span className={treasury?.soldePrevisionnel >= 0 ? 'text-green-700' : 'text-red-700'}>
                          {(treasury?.soldePrevisionnel || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
                        </span>
                        <span className="text-gray-400">-</span>
                      </div>
                    </td>
                  );
                })}
              </tr>

              <tr className="bg-blue-100 border-b-2 border-blue-300">
                <td className="sticky left-0 z-10 bg-blue-100 px-4 py-3 font-bold text-blue-900 border-r border-gray-200">
                  Trésorerie fin de mois
                </td>
                {months.map((monthInfo, idx) => {
                  const monthKey = `${monthInfo.calendarYear}-${String(monthInfo.month).padStart(2, '0')}`;
                  const treasury = monthlyTreasury[monthKey];
                  return (
                    <td key={idx} className="px-3 py-3 text-center border-r border-gray-200">
                      <div className="text-sm font-bold">
                        <span className={treasury?.tresorerieFin >= 0 ? 'text-blue-700' : 'text-red-700'}>
                          {(treasury?.tresorerieFin || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isEssentialView && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Eye className="text-teal-600 mt-0.5" size={18} />
            <div className="text-sm text-teal-900">
              <p className="font-medium mb-1">Vue essentielle activée</p>
              <p className="text-teal-800">
                Cette vue simplifiée affiche uniquement les totaux mensuels pour une vision rapide de votre trésorerie.
                Pour voir le détail par catégorie et modifier les prévisionnels, cliquez sur "Vue détaillée".
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <StickyNote className="text-blue-600 mt-0.5" size={18} />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Comment utiliser le plan de trésorerie :</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li><strong>Réalisé</strong> : montants réels issus de vos transactions</li>
              <li><strong>Prévisionnel</strong> : cliquez sur les montants pour les modifier</li>
              <li><strong>%</strong> : pourcentage de réalisation (vert = bon, orange = attention, rouge = alerte)</li>
              <li><strong>Solde</strong> : différence entre encaissements et décaissements</li>
              <li><strong>Trésorerie</strong> : trésorerie de début + solde = trésorerie de fin</li>
              <li>Les lignes se plient/déplient en cliquant sur "Encaissements" ou "Décaissements"</li>
              <li><strong>Scénarios</strong> : créez plusieurs versions du plan pour tester différentes hypothèses sans écraser le prévisionnel de base</li>
            </ul>
          </div>
        </div>
      </div>

      {showCreateScenarioModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Créer un scénario de trésorerie</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du scénario</label>
                <input
                  type="text"
                  value={newScenario.name}
                  onChange={(e) => setNewScenario({ ...newScenario, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Optimiste, Pessimiste, Réaliste..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newScenario.description}
                  onChange={(e) => setNewScenario({ ...newScenario, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Description optionnelle..."
                />
              </div>
              <p className="text-sm text-gray-600">
                Le scénario sera créé en copiant le prévisionnel de base actuel.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateScenarioModal(false);
                  setNewScenario({ name: '', description: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={createScenario}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditScenarioModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Modifier le scénario</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du scénario</label>
                <input
                  type="text"
                  value={editScenario.name}
                  onChange={(e) => setEditScenario({ ...editScenario, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Optimiste, Pessimiste..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editScenario.description}
                  onChange={(e) => setEditScenario({ ...editScenario, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Description optionnelle..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditScenarioModal(false);
                  setEditScenario({ id: '', name: '', description: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={updateScenario}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {showSyncConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-orange-100 rounded-full">
                <RefreshCw className="text-orange-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Synchroniser le budget vers la trésorerie ?</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Cette fonction est destinée à la <strong>construction</strong> de votre plan de trésorerie à partir du budget prévisionnel.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3 mb-4">
                  <p className="text-sm font-medium text-blue-900">Choisissez les éléments à synchroniser :</p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={syncOptions.syncIncome}
                        onChange={(e) => setSyncOptions({ ...syncOptions, syncIncome: e.target.checked })}
                        className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">Lisser les recettes sur 12 mois</span>
                        <p className="text-xs text-gray-600 mt-0.5">Les encaissements annuels seront répartis uniformément</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={syncOptions.syncExpense}
                        onChange={(e) => setSyncOptions({ ...syncOptions, syncExpense: e.target.checked })}
                        className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">Lisser les dépenses sur 12 mois</span>
                        <p className="text-xs text-gray-600 mt-0.5">Les décaissements annuels seront répartis en mensualités égales</p>
                      </div>
                    </label>
                  </div>
                  <p className="text-xs text-blue-700 italic">
                    Recommandé : Le lissage des dépenses est plus cohérent que celui des recettes pour la plupart des structures.
                  </p>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-orange-900 font-medium">Attention :</p>
                  <ul className="text-sm text-orange-800 space-y-1 ml-4 list-disc">
                    <li>Les données prévisionnelles existantes seront <strong>écrasées</strong></li>
                    <li>Les montants budgétaires annuels seront répartis uniformément sur 12 mois</li>
                    <li>Seule la colonne "Prévisionnel" sera modifiée</li>
                    <li>Les transactions réelles (colonne "Réalisé") ne seront <strong>pas affectées</strong></li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowSyncConfirmModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmSyncBudgetToTreasury}
                disabled={!syncOptions.syncIncome && !syncOptions.syncExpense}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={18} />
                Oui, synchroniser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
